import { HttpTypes } from '@medusajs/types';

/**
 * Creates an AbortSignal that aborts after `ms` milliseconds,
 * backward-compatible with environments lacking native AbortSignal.timeout.
 */
const createTimeoutSignal = (ms: number): AbortSignal => {
  if (typeof AbortSignal !== 'undefined' && typeof (AbortSignal as any).timeout === 'function') {
    return (AbortSignal as any).timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
};

/**
 * Derives a readable currency symbol from an ISO currency code (e.g., 'usd' -> '$', 'eur' -> '€').
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  if (!currencyCode) return '';
  try {
    const formatted = (0).toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return formatted.replace(/\d/g, '').trim();
  } catch {
    return currencyCode.toUpperCase();
  }
};

/**
 * Resolves country code (ISO 2-letter) using client-side GPS (navigator.geolocation).
 * Uses high-accuracy coordinates and reverse geocoding.
 */
export const detectCountryFromGPS = async (): Promise<string | null> => {
  if (typeof window === 'undefined' || !navigator.geolocation) {
    return null;
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          
          // Primary: BigDataCloud free client reverse geocoding API
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
            { signal: createTimeoutSignal(5000) }
          );

          if (response.ok) {
            const data = await response.json();
            if (data.countryCode) {
              return resolve(data.countryCode.toLowerCase());
            }
          }

          // Fallback: OpenStreetMap Nominatim
          const osmResponse = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`,
            { signal: createTimeoutSignal(5000) }
          );

          if (osmResponse.ok) {
            const osmData = await osmResponse.json();
            if (osmData.address?.country_code) {
              return resolve(osmData.address.country_code.toLowerCase());
            }
          }

          resolve(null);
        } catch {
          resolve(null);
        }
      },
      () => {
        // User denied permission or GPS timed out
        resolve(null);
      },
      { timeout: 8000, enableHighAccuracy: true, maximumAge: 60000 }
    );
  });
};

/**
 * Resolves country code (ISO 2-letter) using client-side IP lookup.
 */
export const detectCountryFromIPClient = async (): Promise<string | null> => {
  try {
    // Attempt fast free IP service (api.country.is)
    const res = await fetch('https://api.country.is/', {
      signal: createTimeoutSignal(4000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.country) {
        return data.country.toLowerCase();
      }
    }
  } catch {
    // Continue to fallback
  }

  try {
    // Fallback: ipapi.co
    const res = await fetch('https://ipapi.co/json/', {
      signal: createTimeoutSignal(4000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.country_code) {
        return data.country_code.toLowerCase();
      }
    }
  } catch {
    // Ignore error
  }

  return null;
};

export interface ResolvedRegionOption {
  country: string;
  regionId: string;
  currencyCode: string;
  currencySymbol: string;
  displayName: string;
}

/**
 * Matches a detected country code against available Medusa regions.
 * If not found, falls back to the default US / USD region.
 */
export const findBestRegionOption = (
  countryCode: string | null | undefined,
  regions: HttpTypes.StoreRegion[]
): ResolvedRegionOption | null => {
  if (!regions || regions.length === 0) return null;

  const normalizedCode = (countryCode || '').toLowerCase().trim();

  // 1. Direct match: Check if countryCode is in one of the regions
  if (normalizedCode) {
    for (const r of regions) {
      const match = r.countries?.find((c) => c.iso_2?.toLowerCase() === normalizedCode);
      if (match) {
        return {
          country: match.iso_2?.toLowerCase() || normalizedCode,
          regionId: r.id,
          currencyCode: (r.currency_code || 'usd').toUpperCase(),
          currencySymbol: getCurrencySymbol(r.currency_code || 'usd'),
          displayName: match.display_name || match.iso_2?.toUpperCase() || normalizedCode.toUpperCase(),
        };
      }
    }
  }

  // 2. Fallback: Search for 'us' country
  for (const r of regions) {
    const usMatch = r.countries?.find((c) => c.iso_2?.toLowerCase() === 'us');
    if (usMatch) {
      return {
        country: 'us',
        regionId: r.id,
        currencyCode: (r.currency_code || 'usd').toUpperCase(),
        currencySymbol: getCurrencySymbol(r.currency_code || 'usd'),
        displayName: usMatch.display_name || 'United States',
      };
    }
  }

  // 3. Fallback: Search for any region with 'usd' currency
  const usdRegion = regions.find((r) => r.currency_code?.toLowerCase() === 'usd');
  if (usdRegion && usdRegion.countries?.length) {
    const firstCountry = usdRegion.countries[0];
    return {
      country: firstCountry.iso_2?.toLowerCase() || 'us',
      regionId: usdRegion.id,
      currencyCode: (usdRegion.currency_code || 'usd').toUpperCase(),
      currencySymbol: getCurrencySymbol(usdRegion.currency_code || 'usd'),
      displayName: firstCountry.display_name || 'United States',
    };
  }

  // 4. Ultimate fallback: First available region
  const firstR = regions[0];
  const firstC = firstR.countries?.[0];
  return {
    country: firstC?.iso_2?.toLowerCase() || 'us',
    regionId: firstR.id,
    currencyCode: (firstR.currency_code || 'usd').toUpperCase(),
    currencySymbol: getCurrencySymbol(firstR.currency_code || 'usd'),
    displayName: firstC?.display_name || 'United States',
  };
};
